import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Palette, Sparkles, FolderPlus, ImagePlus, FileSpreadsheet } from 'lucide-react';
import { PrimaryButton, OutlineButton } from '../Buttons';

/**
 * One-time onboarding prompt for interior designers who have not yet created any
 * portfolio items. Encourages them to set up at least one portfolio entry so
 * they can start showcasing designs to buyers.
 */
function PortfolioSetupPrompt({ isOpen, onDismiss }) {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleSetupNow = () => {
        onDismiss?.();
        navigate('/interior-designer/add-portfolio');
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="id-portfolio-prompt-title"
        >
            <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
                <button
                    type="button"
                    onClick={onDismiss}
                    className="absolute top-4 right-4 inline-flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 transition-colors border border-gray-200 rounded-md hover:border-gray-300 bg-white/70 backdrop-blur"
                    aria-label="Dismiss portfolio setup prompt"
                >
                    <X size={16} />
                </button>

                <div className="bg-gradient-to-br from-flamingo-500 via-flamingo-400 to-amber-300 px-6 pt-7 pb-6 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 backdrop-blur">
                            <Palette size={26} className="text-white" aria-hidden />
                        </div>
                        <div>
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide">
                                <Sparkles size={12} aria-hidden />
                                Welcome
                            </div>
                            <h2
                                id="id-portfolio-prompt-title"
                                className="mt-2 text-xl font-bold leading-tight"
                            >
                                Set up your portfolio
                            </h2>
                        </div>
                    </div>
                    <p className="mt-3 text-sm text-white/90 leading-relaxed">
                        Add at least one portfolio item so buyers can browse your work
                        and you can attach designs to incoming leads.
                    </p>
                </div>

                <div className="px-6 py-5">
                    <p className="text-sm text-gray-600 mb-4">
                        Each portfolio item helps you showcase what you can deliver. You'll be able to add:
                    </p>
                    <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-flamingo-50 text-flamingo-600">
                                <ImagePlus size={18} aria-hidden />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">Design photos &amp; metadata</div>
                                <div className="text-xs text-gray-500">
                                    Upload images and tag rooms, themes and styles.
                                </div>
                            </div>
                        </li>
                        <li className="flex items-start gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-flamingo-50 text-flamingo-600">
                                <FileSpreadsheet size={18} aria-hidden />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">BOQ &amp; pricing</div>
                                <div className="text-xs text-gray-500">
                                    Capture cost details so quotes can flow straight from leads.
                                </div>
                            </div>
                        </li>
                        <li className="flex items-start gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-flamingo-50 text-flamingo-600">
                                <FolderPlus size={18} aria-hidden />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">Reusable across leads</div>
                                <div className="text-xs text-gray-500">
                                    Attach the same portfolio item to multiple buyer requests.
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
                    <OutlineButton
                        type="button"
                        onClick={onDismiss}
                        className="!w-full sm:!w-auto"
                    >
                        Maybe later
                    </OutlineButton>
                    <PrimaryButton
                        type="button"
                        onClick={handleSetupNow}
                        className="!w-full sm:!w-auto inline-flex items-center justify-center gap-2"
                    >
                        <FolderPlus size={16} aria-hidden />
                        Set up now
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );
}

export default PortfolioSetupPrompt;
